import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import type { CoverageLabel } from '../vocab'
import { clientContextQuerySchema } from './coverage'

/**
 * Dashboards (S1, I-07, INV-012/013, GAP-UI-023/030) and the complete list of
 * published updates (GAP-UI-004, INV-001..004). Every number is computed on
 * the server within the caller's authorized and subscribed scope (P-ID-003).
 */

export const UPDATE_KINDS = ['assessments', 'milestones', 'dialogues'] as const
export type UpdateKind = (typeof UPDATE_KINDS)[number]

export const updatesQuerySchema = pageQuerySchema.extend(clientContextQuerySchema.shape).extend({
  kind: z.enum(UPDATE_KINDS).optional(),
  issuer: z.string().max(64).optional(),
})

/** One released record (INV-002): an assessment, an evidence review or correspondence. */
export type PublishedUpdate = {
  id: string
  kind: UpdateKind
  version: number
  issuer_id: string | null
  company: string
  /** A subscribed case only; null when none is in scope (INV-003). */
  engagement_id: string | null
  title: string
  summary: string
  published_at: string
}

export type DashboardMeeting = {
  id: string
  title: string
  meeting_type: string
  starts_at: string
  ends_at: string
  /** The organiser's display zone (IANA); instants stay UTC. */
  timezone: string
  location: string
  engagement_id: string
  company: string
}

export type DashboardCase = {
  id: string
  issuer_id: string
  company: string
  title: string
  theme: string | null
  status: string
  next_action: string | null
  due_date: string | null
  objectives: number
  /** Objectives whose current stage is S3 or later. */
  objectives_with_implementation: number
  /** Current stage per objective, in objective order (STAT-013). */
  stages: string[]
  /** Σ stage levels / (objectives × 4), 0..1 (INV-014). */
  progress: number
}

export type HoldingAlert = {
  kind: 'adverse_finding' | 'incident' | 'review_overdue'
  issuer_id: string
  company: string
  title: string
  date: string | null
}

export type InvestorDashboard = {
  org_id: string
  server_time: string
  kpis: {
    companies_in_coverage: number
    holdings: number
    portfolios: number
    open_engagements: number
    objectives: number
    /** stalled cases + mapping exceptions (INV-012). */
    need_attention: number
    released_records: number
    open_requests: number
  }
  /** The "Need attention" breakdown (GAP-UI-030). */
  need_attention: {
    stalled_cases: number
    mapping_exceptions: number
    review_overdue: number
    stalled: { id: string; company: string; title: string; due_date: string | null }[]
    exceptions: {
      holding_id: string
      identifier_type: string
      identifier_value: string
      portfolio: string
      mapping_state: string
    }[]
  }
  latest_updates: PublishedUpdate[]
  upcoming_meetings: DashboardMeeting[]
  /** Up to 3 open cases with the earliest due date. */
  attention_cases: DashboardCase[]
  /** Active cases with per-objective stages (R-13: no stage distribution KPI). */
  programme: DashboardCase[]
  alerts: HoldingAlert[]
  coverage_labels: Partial<Record<CoverageLabel, number>>
}

export type WorkItem = {
  kind: 'assessments' | 'milestones' | 'dialogues' | 'minutes' | 'engagements' | 'objectives'
  id: string
  title: string
  company: string | null
  state: string
  updated_at: string
  engagement_id: string | null
}

export type StaffClientRow = {
  org_id: string
  name: string
  holdings: number
  open_exceptions: number
  overdue_reviews: number
  open_questions: number
  open_requests: number
}

export type StaffDashboard = {
  server_time: string
  queue: {
    awaiting_review: number
    awaiting_publication: number
    my_reviews: number
    my_drafts: number
    open_questions: number
    open_requests: number
    requests_assigned_to_me: number
    review_overdue: number
    mapping_exceptions: number
    new_signals: number
  }
  review_items: WorkItem[]
  my_cases_due: {
    id: string
    company: string
    title: string
    status: string
    next_action: string
    due_date: string
    overdue: boolean
  }[]
  clients: StaffClientRow[]
  /** The engagement-manager view: the caller's cases, or every manager's. */
  manager: ManagerView
}

export const MANAGER_SCOPES = ['mine', 'all'] as const
export type ManagerScope = (typeof MANAGER_SCOPES)[number]

export const staffDashboardQuerySchema = z.object({
  managers: z.enum(MANAGER_SCOPES).default('mine'),
})

/** One actionable line; links go to the case or the meeting. */
export type ManagerRow = {
  kind: 'case' | 'objective' | 'milestone' | 'dialogue' | 'meeting' | 'questions' | 'requests'
  id: string
  engagement_id: string
  meeting_id: string | null
  company: string
  case_title: string
  /** What the row is about: the action, objective, meeting or correspondence. */
  title: string
  detail: string | null
  /** The due date, meeting start or correspondence date. */
  date: string | null
  /** Meeting rows: the organiser's display zone for `date`. */
  timezone: string | null
  manager_name: string | null
}

export type ManagerView = {
  scope: ManagerScope
  /** Past-due case and objective actions, and evidence reviews not yet released. */
  overdue: ManagerRow[]
  /** Meetings in the next 14 days. */
  upcoming_meetings: ManagerRow[]
  /** Correspondence awaiting a company reply, unanswered questions, open requests. */
  outstanding: ManagerRow[]
  /** Case and objective due dates in the next 30 days. */
  deadlines: ManagerRow[]
  /** Records on these cases submitted and waiting for a reviewer. */
  drafts_awaiting_review: number
}
